import { FC, memo, useMemo, useState, useCallback } from 'react'
import styles from 'components/features/pfm/KeyValueList/KeyValueList.styl'
import cn from 'classnames'
import SvgIcon from 'components/atoms/SvgIcon'
import ArrowDynamicsIcon from 'icons/arrow-dynamics.svg'
import Scrollable from 'components/atoms/Scrollable'
import Button from 'components/features/pfm/Button/Button'
import { StrippedFragment } from 'components/features/pfm/StrippedFragment/StrippedFragment'

export type KeyValueListItem = {
  name: string
  value: string
  isHeader?: boolean
  dynamics: 'positive' | 'negative' | null
  color?: string
}

type KeyValueListItemProps = KeyValueListItem

const Item: FC<KeyValueListItemProps> = (props) => {
  const { name, value, isHeader, dynamics, color } = props
  const className = cn(styles.itemWrapper, {
    [styles.isHeader]: isHeader,
  })
  const dynamicsSignClassname = cn(styles.dynamicsSignWrapper, {
    [styles.dynamicsSignIsPositive]: dynamics === 'positive',
    [styles.dynamicsSignIsNegative]: dynamics === 'negative',
  })
  const getDynamicsIcon = () => {
    if (dynamics === 'positive') {
      return (
        <div className={ dynamicsSignClassname }>
          <SvgIcon src={ ArrowDynamicsIcon } />
        </div>
      )
    }
    if (dynamics === 'negative') {
      return (
        <div className={ dynamicsSignClassname }>
          <SvgIcon src={ ArrowDynamicsIcon } />
        </div>
      )
    }
    return null
  }
  return (
    <div className={ className }>
      <div className={ styles.leftSide }>
        { color && (
          <div className={ styles.coloredCircle } style={{ backgroundColor: color }} />
        ) }
        <span>{ name }</span>
      </div>
      <div className={ styles.rightSide }>
        { getDynamicsIcon() }
        <div className={ styles.valueWrapper }>
          <span>{ value }</span>
        </div>
      </div>
    </div>
  )
}

type KeyValueListProps = {
  items: KeyValueListItem[]
  isScrollable?: boolean
  limit?: number
}

export const KeyValueList: FC<KeyValueListProps> = memo((props) => {
  const { items, isScrollable, limit } = props

  const [isExpanded, setIsExpanded] = useState(false)
  const sliceCount = isExpanded ? items.length : limit

  const itemsChildren = items
    .slice(0, sliceCount)
    .map((item: KeyValueListItem) => (
      <Item { ...item } key={ item.name } />
    ))

  const onSeeMoreClick = useCallback(() => setIsExpanded(true), [])

  const Wrapper = useMemo<FC>(() => {
    if (isScrollable) {
      return ({ children }) => (
        <Scrollable allowOuterScroll alwaysShowScrollbar flex={ 1 }>
          <div className={ styles.scrollableInner }>
            { children }
          </div>
        </Scrollable>
      )
    }

    return StrippedFragment

  }, [isScrollable])

  return (
    <Wrapper>
      <div className={ styles.keyValueList }>
        { itemsChildren }
        { limit && !isExpanded && (
          <div className={ styles.footerWrapper }>
            <Button style='textOnly' size='mini' onClick={ onSeeMoreClick }>SEE MORE</Button>
          </div>
        ) }
        { !items.length && (
          <div className={ styles.noData }>
            <div>
              No data
            </div>
          </div>
        ) }
      </div>
    </Wrapper>
  )
})
